'use client';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCountryCallingCode,
  parsePhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';
import { cn, getDefaultCountryFromLocale } from '@ais-ui/utils';
import { inputStyles } from './Input';
import { CountryCodeSelect } from './CountryCodeSelect';

export interface PhoneInputProps {
  /** Current value as an E.164 string (e.g. `+4791234567`). */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Forwarded so the surrounding `FormLabel`/description stay associated
   * with the actual number input rather than being silently dropped. */
  id?: string;
  name?: string;
  onBlur?: () => void;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

/**
 * Best-effort E.164 conversion. Uses the selected country so national trunk
 * prefixes (e.g. a leading "0") are stripped correctly; if the number isn't
 * parseable yet it falls back to naive concatenation so input is never lost.
 */
function toE164(digits: string, country: CountryCode): string {
  if (!digits) return '';
  try {
    return parsePhoneNumber(digits, country).number;
  } catch {
    return `+${getCountryCallingCode(country)}${digits}`;
  }
}

/**
 * Single-value companion to `PhoneListInput`: the same country-aware split
 * field (`CountryCodeSelect` + national-number input) but for a lone number
 * rather than a chip list. Emits E.164 strings so it drops straight into a
 * `FormField` and stores the same format as the rest of the app.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled = false,
      id,
      name,
      onBlur,
      className,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    },
    ref,
  ) => {
    const [country, setCountry] = useState<CountryCode>('DK');
    const [national, setNational] = useState('');
    // Tracks the E.164 value we last emitted so the sync-from-prop effect can
    // skip the round-trip and avoid clobbering in-progress typing.
    const lastEmitted = useRef<string | undefined>(undefined);

    // Preselect the country from the browser locale once on mount — no
    // permission prompt, no network call (see getDefaultCountryFromLocale).
    useEffect(() => {
      setCountry(getDefaultCountryFromLocale());
    }, []);

    // Re-derive the displayed national number when the value is changed from
    // the outside (form reset, programmatic set) — but not for our own edits.
    useEffect(() => {
      if (value === lastEmitted.current) return;
      if (!value) {
        setNational('');
        return;
      }
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed?.country) {
          setCountry(parsed.country);
          setNational(parsed.nationalNumber);
          return;
        }
      } catch {
        // Not parseable — render the raw value verbatim.
      }
      setNational(value);
    }, [value]);

    const handleNumberChange = useCallback(
      (raw: string) => {
        // Paste-assist: a full international number switches the country
        // picker to match, keeping just the national part in the field.
        if (raw.startsWith('+')) {
          try {
            const parsed = parsePhoneNumber(raw);
            if (parsed?.country) {
              setCountry(parsed.country);
              setNational(parsed.nationalNumber);
              lastEmitted.current = parsed.number;
              onChange?.(parsed.number);
              return;
            }
          } catch {
            // Still typing — fall through and keep the raw input.
          }
        }
        setNational(raw);
        const e164 = toE164(raw.replace(/\D/g, ''), country);
        lastEmitted.current = e164;
        onChange?.(e164);
      },
      [country, onChange],
    );

    const handleCountryChange = useCallback(
      (nextCountry: CountryCode) => {
        setCountry(nextCountry);
        const e164 = toE164(national.replace(/\D/g, ''), nextCountry);
        lastEmitted.current = e164;
        onChange?.(e164);
      },
      [national, onChange],
    );

    return (
      <div className={cn('flex items-stretch rounded-md shadow-xs', className)}>
        <CountryCodeSelect
          value={country}
          onChange={handleCountryChange}
          disabled={disabled}
        />
        <input
          ref={ref}
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          aria-autocomplete="none"
          disabled={disabled}
          value={national}
          onChange={(e) => handleNumberChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          className={cn(
            inputStyles,
            'flex-1 min-w-0 rounded-l-none shadow-none tabular-nums',
          )}
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';
